import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Layout } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { ArrowLeft, Phone, Mail, UserCheck, UserX, Search } from "lucide-react";

const REASONS = [
    { value: "excused", label: "Me arsye" },
    { value: "sick", label: "Sëmurë" },
    { value: "training", label: "Trajnim" },
    { value: "unexcused", label: "Pa arsye" },
];

const reasonChip = (r) => ({
    excused: "bg-amber-50 text-amber-700 border-amber-200",
    sick: "bg-seablue/10 text-seablue border-seablue/30",
    training: "bg-oxford/10 text-oxford border-oxford/30",
    unexcused: "bg-terracotta/10 text-terracotta border-terracotta/30",
}[r] || "bg-slate-100 text-slate-700 border-slate-200");

export default function SecretaryStaff() {
    const [staff, setStaff] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState("");
    const [dlg, setDlg] = useState(null);
    const [form, setForm] = useState({ reason: "excused", note: "" });
    const [busy, setBusy] = useState(false);

    const refresh = async () => {
        try {
            const r = await api.get("/secretary/staff");
            setStaff(r.data);
        } finally { setLoading(false); }
    };
    useEffect(() => { refresh(); }, []);

    const filtered = staff.filter((s) => !filter || s.name.toLowerCase().includes(filter.toLowerCase()) || (s.email || "").toLowerCase().includes(filter.toLowerCase()));

    const submit = async (e) => {
        e.preventDefault(); setBusy(true);
        try {
            await api.post("/secretary/staff-absence", { staff_id: dlg.id, reason: form.reason, note: form.note });
            setDlg(null);
            setForm({ reason: "excused", note: "" });
            refresh();
        } finally { setBusy(false); }
    };

    const removeAbs = async (s) => {
        if (!s.absent_today) return;
        if (!window.confirm("Hiq mungesën?")) return;
        await api.delete(`/secretary/staff-absence/${s.absent_today.id}`);
        refresh();
    };

    return (
        <Layout>
            <Link to="/secretary" className="inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-oxford mb-4">
                <ArrowLeft className="w-4 h-4" /> Mbrapa
            </Link>
            <div className="mb-6">
                <div className="text-xs uppercase tracking-[0.18em] text-slate-500 mb-2">Stafi</div>
                <h1 className="font-heading text-3xl sm:text-4xl font-black text-slate-900 tracking-tight">Mësuesit & Stafi</h1>
                <p className="text-slate-600 mt-2 text-sm">Numra telefoni për kontakt + shëno mungesat e stafit</p>
            </div>

            <div className="bg-white border border-slate-200 rounded-2xl p-3 mb-5 flex items-center gap-2">
                <Search className="w-4 h-4 text-slate-400" />
                <Input placeholder="Kërko sipas emrit ose email-it…" value={filter} onChange={(e) => setFilter(e.target.value)} className="border-0 focus-visible:ring-0" data-testid="staff-filter" />
                <span className="text-xs text-slate-500">{filtered.length} / {staff.length}</span>
            </div>

            {loading ? <div className="text-slate-500">Duke ngarkuar…</div> : (
                <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
                    <div className="divide-y divide-slate-200">
                        {filtered.map((s) => {
                            const roleLabel = s.role === "teacher" ? "Mësues" : s.role === "secretary" ? "Sekretar" : s.role === "super_admin" ? "Admin" : s.role;
                            return (
                                <div key={s.id} className="p-4 flex flex-col sm:flex-row sm:items-center gap-3" data-testid={`staff-row-${s.id}`}>
                                    <div className="flex items-center gap-3 flex-1 min-w-0">
                                        <div className={`w-10 h-10 rounded-xl flex items-center justify-center font-bold text-sm shrink-0 ${s.role === "secretary" ? "bg-seablue/10 text-seablue" : s.role === "super_admin" ? "bg-oxford text-white" : "bg-sage/10 text-sage"}`}>
                                            {s.name.split(" ").map((n) => n[0]).slice(0, 2).join("")}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2">
                                                <span className="font-bold text-slate-900 truncate">{s.name}</span>
                                                <span className="text-[10px] uppercase tracking-widest font-bold text-slate-500 bg-slate-100 px-1.5 py-0.5 rounded">{roleLabel}</span>
                                                {s.absent_today && <span className={`text-[10px] uppercase tracking-widest font-bold px-1.5 py-0.5 rounded border ${reasonChip(s.absent_today.reason)}`}>{REASONS.find((r) => r.value === s.absent_today.reason)?.label || s.absent_today.reason}</span>}
                                            </div>
                                            <div className="text-[11px] text-slate-500 truncate">{s.email}</div>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-2 shrink-0">
                                        {s.phone && (
                                            <a href={`tel:${s.phone.replace(/\s/g, "")}`} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-sage/10 text-sage hover:bg-sage/20 text-xs font-semibold border border-sage/30" data-testid={`staff-call-${s.id}`}>
                                                <Phone className="w-3 h-3" /> {s.phone}
                                            </a>
                                        )}
                                        {s.email && (
                                            <a href={`mailto:${s.email}`} className="inline-flex items-center justify-center w-8 h-8 rounded-md bg-oxford/10 text-oxford hover:bg-oxford/20 border border-oxford/20" title={s.email}>
                                                <Mail className="w-3.5 h-3.5" />
                                            </a>
                                        )}
                                        {s.absent_today ? (
                                            <button onClick={() => removeAbs(s)} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-terracotta text-white hover:bg-terracotta/90 text-xs font-semibold" data-testid={`staff-unmark-${s.id}`}>
                                                <UserCheck className="w-3 h-3" /> Hiq mungesën
                                            </button>
                                        ) : (
                                            <button onClick={() => { setDlg(s); setForm({ reason: "excused", note: "" }); }} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-slate-200 hover:border-terracotta hover:text-terracotta text-xs font-semibold" data-testid={`staff-mark-${s.id}`}>
                                                <UserX className="w-3 h-3" /> Shëno mungesë
                                            </button>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}

            <Dialog open={!!dlg} onOpenChange={(v) => !v && setDlg(null)}>
                <DialogContent>
                    <DialogHeader><DialogTitle className="font-heading">Shëno mungesë për {dlg?.name}</DialogTitle></DialogHeader>
                    <form onSubmit={submit} className="space-y-3">
                        <div>
                            <label className="text-xs font-bold uppercase tracking-widest text-slate-500 block mb-2">Arsyeja</label>
                            <div className="grid grid-cols-2 gap-2">
                                {REASONS.map((r) => (
                                    <button key={r.value} type="button" onClick={() => setForm({ ...form, reason: r.value })}
                                        className={`px-3 py-2 rounded-md border text-xs font-semibold ${form.reason === r.value ? "bg-oxford text-white border-oxford" : "bg-white border-slate-200 hover:border-oxford"}`}
                                        data-testid={`staff-reason-${r.value}`}>{r.label}</button>
                                ))}
                            </div>
                        </div>
                        <Textarea placeholder="Shënim opsional…" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} rows={2} data-testid="staff-note" />
                        <DialogFooter>
                            <Button type="submit" disabled={busy} className="bg-oxford hover:bg-oxfordhover text-white" data-testid="staff-mark-submit">{busy ? "Duke ruajtur…" : "Shëno"}</Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>
        </Layout>
    );
}
